import { find, get, toLower } from 'lodash';
import { MARKET } from '@nandosaus/constants';
import { observer } from 'mobx-react';
import { restaurantPropTypes } from '@nandosaus/prop-types';
import { useRootStore } from '@nandosaus/state-management';
import { useRouter } from 'next/router';
import gql from 'graphql-tag';
import React, { useEffect } from 'react';
import styled from 'styled-components';

import { australianStates, nzRegions } from '../../../constants';
import { Box, Col, Container, Row } from '../../../components/grid';
import { convertToSlug } from '../../../utils/convert-to-slug';
import { decals } from '../../../components/decal/constants';
import { fetch } from '../../../content/queries';
import { Layout } from '../../../components/layout';
import { PageHeaderBlock } from '../../../components/content-image';
import { RestaurantMap } from '../../../components/restaurant-map';
import { RestaurantsList } from '../../../components/restaurant-list';
import { useIsMinWidth } from '../../../hooks/use-is-min-width';
import { useMarket } from '../../../hooks/use-market';

const getStateLongName = (market, state) => {
  const isAusMarket = process.env.market === MARKET.AUSTRALIA;
  const states = isAusMarket ? australianStates : nzRegions;
  return get(
    find(states, ({ value }) => toLower(value) === toLower(state)),
    'longName'
  );
};

const RestaurantsStatePage = observer(({ restaurants, title }) => {
  const router = useRouter();
  const { state } = router.query;
  const { RestaurantStore } = useRootStore();
  const isMobile = !useIsMinWidth('md');

  /**
   * Preload all restaurants when the page is loaded.
   */
  useEffect(() => {
    if (!RestaurantStore.loaded) {
      RestaurantStore.loadRestaurants();
    }
  }, [RestaurantStore]);

  /**
   * If RestaurantStore is loaded, use the restaurant data from state management, otherwise use restaurant data from initial props.
   */
  const restaurantsData = RestaurantStore.loaded ? RestaurantStore.getRestaurantsByState(state) : restaurants;

  const stateLongName = getStateLongName(useMarket(), state);
  return (
    <Layout title={title}>
      <PageHeaderBlock
        heading={`Nando’s ${stateLongName}`}
        leftDecal={decals.restaurants.left}
        leftDecalStyle={{ height: '70%' }}
        rightDecalStyle={{ height: '70%' }}
        rightDecal={decals.restaurants.right}
        contentPosition="center"
        contentAlignment="center"
      />
      <Container flexDirection="column" mt="2rem" flex={1}>
        <Row flexWrap="wrap" flexDirection={{ xs: 'column-reverse', lg: 'row' }}>
          <Col width={{ xs: 12 / 12, lg: 5 / 12 }}>
            {restaurants && <RestaurantsList variant="card" restaurants={restaurantsData} showRestaurantStatus />}
          </Col>
          <Col position="relative" width={{ xs: 12 / 12, lg: 7 / 12 }} mb={{ xs: 2, lg: 0 }}>
            {RestaurantStore.loaded && (
              <MapWrapper bg="white" padding="0.25rem">
                <RestaurantMap
                  height={isMobile ? '500px' : '629px'}
                  coordinates={null}
                  linkText="View restaurant info"
                  restaurants={restaurantsData}
                  showRestaurantStatus
                />
              </MapWrapper>
            )}
          </Col>
        </Row>
      </Container>
    </Layout>
  );
});

const MapWrapper = styled(Box)`
  position: sticky;
  top: 1rem;
`;

export async function getStaticPaths() {
  const isAusMarket = process.env.market === MARKET.AUSTRALIA;
  const states = isAusMarket ? australianStates : nzRegions;

  return {
    paths: states.map(state => ({
      params: {
        state: convertToSlug(state.value),
      },
    })),
    fallback: false,
  };
}

export async function getStaticProps({ params }) {
  const { state } = params;

  const stateLongName = getStateLongName(process.env.market, state);
  const title = `Nando's | ${stateLongName} Restaurant Locations`;

  const contentQuery = gql`
    query getStaticRestaurantDataByState($state: String!) {
      restaurants(state: $state) {
        id
        name
        longitude
        latitude
        phone
        address {
          address1
          address2
          country
          postcode
          state
          suburb
        }
      }
    }
  `;

  const data = await fetch({
    query: contentQuery,
    variables: { state },
  });

  return {
    props: { restaurants: data.restaurants, title },
  };
}

RestaurantsStatePage.propTypes = {
  restaurant: restaurantPropTypes.isRequired,
};

export default RestaurantsStatePage;
